import React, { useState, useEffect, useRef } from 'react';
import AdminLayout from '../components/AdminLayout';
import { useMediaLibrary, useMediaUploader } from '../../hooks/useMedia';

export default function MediaPage() {
  const { media, loading, pagination, fetchMedia, goToPage, removeMedia, addMedia } = useMediaLibrary();
  const { upload, uploading, progress, error: uploadError, isDragging, dropZoneProps } = useMediaUploader({
    onSuccess: (newMedia) => addMedia(newMedia),
    onError: (err) => alert('Upload error: ' + err.message)
  });

  const fileInputRef = useRef(null);
  const [selectedMedia, setSelectedMedia] = useState(null);

  useEffect(() => {
    fetchMedia();
  }, []);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      await upload(file);
    }
    e.target.value = '';
  };

  const handleDelete = async (id, filename) => {
    if (!window.confirm(`Delete "${filename}"?`)) return;
    try {
      await removeMedia(id);
      if (selectedMedia?.id === id) setSelectedMedia(null);
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const copyToClipboard = (url) => {
    navigator.clipboard.writeText(url);
    alert('URL copied to clipboard!');
  };

  return (
    <AdminLayout title="Media Library">
      {/* Upload Area */}
      <div
        className="admin-card"
        {...dropZoneProps}
        style={{
          border: isDragging ? '2px dashed var(--admin-primary)' : '2px dashed var(--admin-border)',
          background: isDragging ? 'rgba(37, 99, 235, 0.05)' : 'var(--admin-card-bg)'
        }}
      >
        <div className="admin-card-body" style={{ textAlign: 'center', padding: '40px 20px' }}>
          <div style={{ marginBottom: '16px' }}>
            <svg width="48" height="48" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ opacity: 0.5 }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>

          {uploading ? (
            <div>
              <div className="admin-spinner" style={{ margin: '0 auto 16px' }}></div>
              <p>Uploading... {progress}%</p>
            </div>
          ) : (
            <>
              <p style={{ marginBottom: '16px', color: 'var(--admin-text-muted)' }}>
                {isDragging ? 'Drop images here' : 'Drag and drop images here, or click to select'}
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="admin-btn admin-btn-primary"
              >
                Select Files
              </button>
            </>
          )}

          {uploadError && (
            <p style={{ color: 'var(--admin-danger)', marginTop: '16px' }}>{uploadError}</p>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: selectedMedia ? '1fr 300px' : '1fr', gap: '24px' }}>
        {/* Media Grid */}
        <div className="admin-card">
          <div className="admin-card-header">
            <h3 className="admin-card-title">
              All Media ({pagination.total})
            </h3>
          </div>
          <div className="admin-card-body">
            {loading ? (
              <div className="admin-loading">
                <div className="admin-spinner"></div>
              </div>
            ) : media.length === 0 ? (
              <div className="admin-empty">
                <div className="admin-empty-icon">🖼️</div>
                <div className="admin-empty-title">No media yet</div>
                <p>Upload images to get started</p>
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                gap: '12px'
              }}>
                {media.map(item => (
                  <div
                    key={item.id}
                    onClick={() => setSelectedMedia(item)}
                    style={{
                      aspectRatio: '1',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      border: selectedMedia?.id === item.id
                        ? '3px solid var(--admin-primary)'
                        : '1px solid var(--admin-border)',
                      background: '#f1f5f9'
                    }}
                  >
                    <img
                      src={item.thumbnailUrl || item.url}
                      alt={item.altText || item.originalName}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="admin-pagination">
              <button
                className="admin-pagination-btn"
                onClick={() => goToPage(pagination.page - 1)}
                disabled={pagination.page === 1}
              >
                Previous
              </button>
              <span className="admin-text-muted">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button
                className="admin-pagination-btn"
                onClick={() => goToPage(pagination.page + 1)}
                disabled={pagination.page === pagination.totalPages}
              >
                Next
              </button>
            </div>
          )}
        </div>

        {/* Selected Media Details */}
        {selectedMedia && (
          <div className="admin-card">
            <div className="admin-card-header">
              <h3 className="admin-card-title">Details</h3>
              <button
                onClick={() => setSelectedMedia(null)}
                className="admin-btn admin-btn-icon admin-btn-secondary"
              >
                ×
              </button>
            </div>
            <div className="admin-card-body">
              <img
                src={selectedMedia.url}
                alt={selectedMedia.altText || selectedMedia.originalName}
                style={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: '8px',
                  marginBottom: '16px'
                }}
              />

              <div style={{ fontSize: '13px' }}>
                <p><strong>Filename:</strong><br />{selectedMedia.originalName}</p>
                <p><strong>Dimensions:</strong><br />{selectedMedia.width} × {selectedMedia.height}</p>
                <p><strong>Size:</strong><br />{(selectedMedia.fileSize / 1024).toFixed(1)} KB</p>
                <p><strong>Type:</strong><br />{selectedMedia.mimeType}</p>
                <p><strong>Uploaded:</strong><br />{new Date(selectedMedia.createdAt).toLocaleString()}</p>
              </div>

              <div className="admin-form-group" style={{ marginTop: '16px' }}>
                <label className="admin-label">URL</label>
                <input
                  type="text"
                  className="admin-input"
                  value={selectedMedia.url}
                  readOnly
                  style={{ fontSize: '12px' }}
                />
              </div>

              <div className="admin-flex admin-gap-2" style={{ marginTop: '16px' }}>
                <button
                  onClick={() => copyToClipboard(selectedMedia.url)}
                  className="admin-btn admin-btn-secondary"
                  style={{ flex: 1 }}
                >
                  Copy URL
                </button>
                <button
                  onClick={() => handleDelete(selectedMedia.id, selectedMedia.originalName)}
                  className="admin-btn admin-btn-danger"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
